import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";
import { getBrandOptions } from "@/api";
import { EntityDetailPage } from "@/components/build/EntityDetailPage";
import { EditWithAiButton } from "@/features/build/withAi/EditWithAiButton";
import { BrandAccessTab } from "./BrandAccessTab";
import { BrandForm } from "./BrandForm";
import { BrandKitTab } from "./BrandKitTab";
import { BrandTemplatesTab } from "./templates/BrandTemplatesTab";
import { useDeleteBrand } from "./useDeleteBrand";

/**
 * Build > Brands > detail (docs/design/web/WEB_BUILD_PATTERN_PLAN.md §2): the shared
 * EntityDetailPage scaffold; Overview (identity form), Kit (palette/fonts),
 * Templates (PowerPoint/Word), Access tabs.
 */
export function BrandDetailPage() {
  const { t } = useTranslation(["build", "brands"]);
  const navigate = useNavigate();
  const { uuid = "" } = useParams();
  const brand = useQuery(getBrandOptions({ path: { brand_uuid: uuid } }));
  const { onDelete, dialog } = useDeleteBrand(() => navigate("/build/brands"));
  const data = brand.data?.data;

  return (
    <EntityDetailPage
      listPath="/build/brands"
      rootLabel={t("brands:title")}
      name={data?.name ?? ""}
      isPending={brand.isPending}
      isError={brand.isError}
      notFoundLabel={t("brands:detail.notFound")}
      tabs={[
        { value: "overview", label: t("brands:tabs.overview"), content: data && <BrandForm brand={data} /> },
        { value: "kit", label: t("brands:tabs.kit"), content: data && <BrandKitTab brand={data} /> },
        {
          value: "templates",
          label: t("brands:tabs.templates"),
          content: data && <BrandTemplatesTab brand={data} />,
        },
        { value: "access", label: t("brands:tabs.access"), content: data && <BrandAccessTab brand={data} /> },
      ]}
      canDelete={data?.canManage ?? false}
      onDelete={() => data && onDelete(data)}
      deleteDialog={dialog}
      extraActions={data?.canEdit ? <EditWithAiButton entityType="brand" uuid={data.uuid} /> : undefined}
    />
  );
}
